import { useEffect, useState }         from 'react';
import api                             from '../../services/api';
import { useMassBalancePage }          from '../../contexts/MassBalancePageContext';
import MassBalanceHeatmapTable         from './MassBalanceHeatmapTable';

const DATE_WINDOW_SIZE = 16;

function gerarDatasDaJanela(dataInicial, tamanho) {
    const [ano, mes, dia] = dataInicial.split('-').map(Number);

    return Array.from({ length: tamanho }, (_, indice) => (
        new Date(Date.UTC(ano, mes - 1, dia + indice)).toISOString().slice(0, 10)
    ));
}

function MassBalanceHeatmapTableContainer() {

    const { selectedTerminal, selectedDate } = useMassBalancePage();

    const [dados, setDados]     = useState([]);
    const [colunas, setColunas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {

        const fetchHeatmapData = async () => {

            if (!selectedTerminal || !selectedDate) {
                setDados([]);
                setColunas([]);
                return;
            }

            try {
                setLoading(true);

                const dateWindow = gerarDatasDaJanela(selectedDate, DATE_WINDOW_SIZE);

                // "Todos" traz a lista de terminais; um terminal só vira uma lista de um.
                const terminais = selectedTerminal.isAll ? selectedTerminal.terminals : [selectedTerminal];

                const linhasPorTerminal = await Promise.all(
                    terminais.map(async (terminal) => {

                        const productsResponse = await api.get(`/getTerminalProductId/${terminal.id}`);
                        const products = productsResponse.data || [];

                        return Promise.all(
                            products.map(async (product) => {

                                // Uma única postagem já traz os registros de todos os dias
                                // projetados (cada um com seu próprio campo "date").
                                const response = await api.get('/getMassBalanceFiltered', {
                                    params: {
                                        terminalId: terminal.id,
                                        massBalancePostReferenceDate: selectedDate,
                                        productId: product.id,
                                    },
                                });

                                const registros = (response.data || []).filter((registro) => dateWindow.includes(registro.date));

                                // Pode haver mais de um navio/registro no mesmo dia; para a célula
                                // do heatmap e a Visão Estratégica usamos o mais recente (maior id)
                                // como estado representativo do dia.
                                const registroMaisRecentePorData = new Map();

                                registros.forEach((registro) => {
                                    const atual = registroMaisRecentePorData.get(registro.date);

                                    if (!atual || registro.id > atual.id) {
                                        registroMaisRecentePorData.set(registro.date, registro);
                                    }
                                });

                                const diasRepresentativos = dateWindow.map((data) => (
                                    registroMaisRecentePorData.get(data) || null
                                ));

                                return {
                                    terminal: terminal.name,
                                    produto: product.name,
                                    valores: diasRepresentativos.map((linha) => (
                                        linha && Number(linha.static) > 0
                                            ? {
                                                percentual: Math.min(100, Math.max(0, (Number(linha.final) / Number(linha.static)) * 100)),
                                                volume: Number(linha.final),
                                            }
                                            : null
                                    )),
                                    diasRepresentativos,
                                    registros,
                                };

                            })
                        );

                    })
                );

                const linhas = linhasPorTerminal.flat()
                    .filter((linha) => linha.valores.some((v) => v !== null));

                setDados(linhas);
                setColunas(dateWindow);

            } catch (error) {
                console.error('Erro ao buscar os dados do heatmap de Balanço de Massa:', error);
                setDados([]);
                setColunas([]);
            } finally {
                setLoading(false);
            }

        };

        fetchHeatmapData();

    }, [selectedTerminal, selectedDate]);

    return (
        <MassBalanceHeatmapTable dados={dados} colunas={colunas} loading={loading} />
    );
}

export default MassBalanceHeatmapTableContainer;